// Motion preset registry (docs/plans/v2-motion-primitive.md §6).
//
// The 12 `AnimationToken`s survive as NAMED PRESETS that desugar to tracks of
// the composable primitive. Templates and the LLM keep authoring one word; the
// RenderDoc only ever carries concrete tracks.
//
// Every preset here is a TRANSCRIPTION of the renderer's current preset
// function (packages/remotion/src/registries/animations.ts), channel for
// channel — that is what makes the desugaring a zero-regression change.
// `packages/remotion/src/registries/motion.equivalence.test.ts` proves it
// frame by frame.
//
// Adding a token: add it to ANIMATION_TOKENS, add its tracks here, and the
// Record type makes the omission a compile error.
import { ANIMATION_TOKENS } from './animation-tokens.js';
/** The renderer's DEFAULT_ENTRANCE_FRAMES. One number, one place. */
export const DEFAULT_MOTION_FRAMES = 12;
/** The modal v1 tail-fade length (5 of 16 exit sites use exactly this). */
export const DEFAULT_EXIT_FRAMES = 6;
/**
 * Default slide travel, in px. The legacy `makeSlide` default; the dead
 * `AnimationParams.slideDistance` is what a track's `from` now expresses
 * directly, so nothing has to reach past the registry to change it.
 */
const SLIDE_DISTANCE = 60;
// ── letter-fade-scattered ───────────────────────────────────────────
//
// Both numbers are MEASURED off the source reel (0–3s), not chosen: each letter
// ramps its opacity over ~12 frames, and the letters' start times span ~22
// frames in total with no correlation between a letter's position in the line
// and when it starts (r = -0.23). They live here, next to the preset, because
// the renderer needs the identical values and a second copy over there is how
// the look and the document's idea of the look would drift apart.
//
// The per-letter ramp is what `entranceDurationFrames` overrides on a text
// node — it is the same quantity that field already means for `fade-in`, just
// applied per glyph. The spread is deliberately NOT derived from it: scaling
// the scatter by the ramp was tried and rejected, because a template that
// wants a snappier glyph fade almost never wants a tighter scatter — those are
// two independent halves of the look. A template that wants a different spread
// authors a `reveal` track by hand.
/** Per-letter opacity ramp, in frames. */
export const LETTER_SCATTER_FADE_FRAMES = 12;
/** Total spread of the letters' start times, in frames. */
export const LETTER_SCATTER_SPREAD_FRAMES = 22;
// ── letter-flip ─────────────────────────────────────────────────────
//
// MEASURED off reel-4's hook, by isolating SINGLE GLYPHS as connected
// components and tracking each one's centroid, principal axis and extent
// across its own entrance. Two glyphs, independently, say the same thing:
//
//                  rotation                 slide      vertical   own size
//   'B' of BE    -34.4deg -> -4.4deg      -39.3px       -0.3px    constant
//   'R' of AFRAID -33.6deg -> -8.2deg     -33.5px       -0.3px    constant
//
// So each glyph fades in rotated COUNTER-CLOCKWISE — its up-axis pointing
// north-west — sitting to the RIGHT of where it belongs, then rotates CLOCKWISE
// to upright as it slides left into place. The line reveals left to right, so
// several glyphs are mid-flight at once.
//
// Three things this is NOT, each ruled out by a measurement above:
//   * NOT a rotation in depth. Glyph height never changes, and neither does
//     its width along its own axes.
//   * NOT a scale of any kind. The glyph's extent measured along its OWN
//     principal axes is flat to within a pixel (29.2x40.3 -> 28.9x40.1) for
//     the whole entrance.
//   * NOT a pivot at a corner or an edge. The centroid travels horizontally
//     and does not rise or fall (dy = -0.3px over the whole settle), which is
//     rotation about the glyph's own centre plus a separate horizontal slide.
//
// A NOTE ON HOW THIS WAS GOT WRONG TWICE, because the failure has one cause.
// Both earlier versions measured an AXIS-ALIGNED BOX around a rotating glyph
// and read its changing width as a transform in its own right — first as a
// rotateY about the leading edge, then as a scaleX that springs past its
// resting width and settles. The bounding box of a glyph rotating from -34deg
// to 0 does exactly that: it starts wide and shrinks, with no scaling anywhere.
// Every number in both stories was real; the geometry was not. Measure the
// glyph in ITS OWN frame, not the box the screen draws around it.
/**
 * Per-glyph settle, in frames.
 *
 * The measurable window is ~11 frames, and it covers only about two thirds of
 * the travel — see LETTER_FLIP_START_DEG for why every number here is truncated
 * the same way. Extrapolated to the full settle that is ~17, rounded to 18.
 */
export const LETTER_FLIP_RAMP_FRAMES = 18;
/** Gap between one glyph's start and the next, in frames. */
export const LETTER_FLIP_STAGGER_FRAMES = 2;
/**
 * Starting rotation, in degrees. NEGATIVE is counter-clockwise on screen — the
 * glyph's up-axis points north-west — and it turns clockwise to 0.
 *
 * The sign is the whole character of the animation and it is easy to invert:
 * the measurements above are in image coordinates, where y runs DOWNWARD, so a
 * raw positive shear there is a counter-clockwise lean on screen. An earlier
 * version of this preset had it backwards for exactly that reason.
 *
 * The magnitude is an extrapolation, and the only value here that is: a glyph
 * is not segmentable until its fade has brought it up out of the plate, so the
 * steepest angle actually measurable is ~-34deg a few frames in. Running the
 * measured rate back to the glyph's start lands near -45.
 */
export const LETTER_FLIP_START_DEG = -45;
/**
 * How far right of home a glyph starts, in `em`, sliding left into place.
 *
 * In `em` rather than px because the measurement is a proportion of the type,
 * not of the frame: 39.3px and 33.5px of slide against glyphs ~40px tall, on a
 * 720-wide source. A px constant here would be silently wrong at every size
 * except the one reel it came from.
 *
 * Extrapolated past the measured 39.3px for the same reason the angle is — that
 * figure covers only the visible two thirds of the travel, so the full slide is
 * nearer 59px, or about 1em against this line's type size. The measured value
 * was used first and read as barely moving, which is the tell: a truncated
 * measurement taken literally always under-states the motion.
 */
export const LETTER_FLIP_SLIDE_EM = 1;
/**
 * Fraction of the ramp the opacity takes. The glyph is solid well before it
 * stops moving — measured brightness plateaus around a third of the way through
 * the settle — so the tail of the animation reads as motion, not as a fade.
 */
export const LETTER_FLIP_FADE_FRACTION = 0.3;
/**
 * The most a glyph's ramp may take of the whole assembly.
 *
 * Without this a short line has no room to type: reel-4's list assembles nine
 * glyphs in ~9 frames total, so a fixed 14-frame ramp would leave a negative
 * stagger and land every glyph at once — the animation would still play, and
 * would silently be the wrong one. The hook is unaffected (its 42-frame span
 * leaves the full ramp intact), which is the point: this bounds the short case
 * without touching the case the constants were measured on.
 */
export const LETTER_FLIP_RAMP_FRACTION = 0.6;
/**
 * Split an authored assembly span into a per-glyph ramp and a stagger.
 *
 * Lives here rather than in the renderer because the preset's fixed duration
 * and the block's per-glyph timing have to agree; two copies of this arithmetic
 * is how the document's idea of the cadence and the rendered cadence drift.
 */
export function letterFlipCadence(spanFrames, glyphCount) {
    if (spanFrames === undefined) {
        return {
            rampFrames: LETTER_FLIP_RAMP_FRAMES,
            staggerFrames: LETTER_FLIP_STAGGER_FRAMES,
        };
    }
    const span = Math.max(1, spanFrames);
    const rampFrames = Math.min(LETTER_FLIP_RAMP_FRAMES, span * LETTER_FLIP_RAMP_FRACTION);
    const staggerFrames = glyphCount > 1 ? Math.max(0, (span - rampFrames) / (glyphCount - 1)) : 0;
    return { rampFrames, staggerFrames };
}
/**
 * The whole assembly: the LAST glyph's start plus its own ramp.
 *
 * Same contract as `wordAssemblySpanFrames` — a `reveal` track carries one
 * number and the glyph count is only known in the block, so the track's
 * duration means the entire assembly and the renderer derives each glyph's own
 * window by mapping progress back across it.
 */
export function letterFlipSpanFrames(glyphCount, opts) {
    const ramp = opts?.rampFrames ?? LETTER_FLIP_RAMP_FRAMES;
    const stagger = opts?.staggerFrames ?? LETTER_FLIP_STAGGER_FRAMES;
    return Math.max(1, (Math.max(1, glyphCount) - 1) * stagger + ramp);
}
/**
 * Glyphs the preset's FIXED duration is sized for. Same reasoning as
 * `WORD_REVEAL_NOMINAL_WORDS`: the measured source line `DON'T BE AFRAID TO`
 * carries exactly 15 non-space glyphs, and any other length assembles over the
 * same total span with a proportionally stretched cadence.
 */
const LETTER_FLIP_NOMINAL_GLYPHS = 15;
/** The preset's `reveal` duration: a nominal line's whole assembly. */
export const LETTER_FLIP_SPAN_FRAMES = letterFlipSpanFrames(LETTER_FLIP_NOMINAL_GLYPHS);
// ── word-by-word / word-by-word-reverse ─────────────────────────────
//
// MEASURED off the source (skin-edit 5.0-5.9s @30fps), not chosen. The two
// stacked lines assemble a word at a time: the upper right-to-left with
// corr(x, onset) = -0.70 and onsets 5.07 / 5.10 / 5.13s — 0.03s apart, i.e.
// ~1 frame — and the lower left-to-right at a 2 frame stagger. Both ramp a
// word in over ~2 frames.
//
// ONE cadence is used for both directions, because the direction is not a
// motion property: `word-by-word-reverse` mirrors which word gets which turn
// and nothing else, and a test pins the two as exact mirrors at the same
// frame. The forward line's 2 frames is the value taken — the two
// measurements differ by a single frame, which is inside the noise of reading
// onsets off a 30fps dump, and the slower of the two is the readable one.
//
// This REPLACES the renderer's old hardcoded 4-frame stagger / 7-frame fade,
// which came from the v1 fade-benefits layer rather than from any source
// reel. A four-word headline used to assemble over 19 frames; it now takes 8.
/** Per-word opacity ramp, in frames. */
export const WORD_REVEAL_FADE_FRAMES = 2;
/** Gap between one word's start and the next, in frames. */
export const WORD_REVEAL_STAGGER_FRAMES = 2;
/**
 * The whole assembly: the LAST word's start plus its own ramp.
 *
 * This — not one word's ramp — is what the `reveal` track's duration means,
 * for the same reason it does on `letter-fade-scattered`: `reveal` is "how far
 * through this element's reveal are we", and the line is not revealed until the
 * final word has finished. A single track duration cannot encode a
 * count-dependent cadence, so the renderer maps progress across the whole
 * assembly and derives each word's own window from it.
 */
export function wordAssemblySpanFrames(wordCount, opts) {
    const fade = opts?.fadeFrames ?? WORD_REVEAL_FADE_FRAMES;
    const stagger = opts?.staggerFrames ?? WORD_REVEAL_STAGGER_FRAMES;
    return Math.max(1, (Math.max(1, wordCount) - 1) * stagger + fade);
}
/**
 * Words the preset's FIXED duration is sized for. A track carries one number
 * and the word count is only known in the block, so the preset picks a typical
 * headline line (the source's own "Book any of our" is exactly four) and any
 * other length assembles over the same total span with a proportionally
 * stretched or tightened cadence — precisely how `letter-fade-scattered`
 * already spreads N letters across a fixed 22 frames.
 */
const WORD_REVEAL_NOMINAL_WORDS = 4;
/** The word presets' `reveal` duration: a nominal line's whole assembly. */
export const WORD_REVEAL_SPAN_FRAMES = wordAssemblySpanFrames(WORD_REVEAL_NOMINAL_WORDS);
const fade = (easing = 'linear') => ({
    channel: 'opacity',
    from: 0,
    to: 1,
    duration: DEFAULT_MOTION_FRAMES,
    easing,
});
/**
 * One edge-anchored band that grows to cover the frame.
 *
 * `from` states the band's resting geometry ON its edge — zero-height for a
 * horizontal wipe, zero-width for a vertical one — and every bar preset
 * finishes covering the frame, so only the start differs between them.
 */
const barWipe = (from) => ({
    channel: 'mask',
    from: 0,
    to: 1,
    duration: 18,
    easing: 'ease-in-out',
    mask: { shape: 'rect', from, to: { x: 0, y: 0, w: 1, h: 1 } },
});
// ── thirds-cascade ──────────────────────────────────────────────────
//
// Observed at reel-3 10-18% — frames 87-156 of 869, which is where the 69 comes
// from. `docs/plans/reel-3-thirds-cascade.md` is the spec and carries the full
// provenance table.
//
// SPEC IS INTENT, NOT TRANSCRIPTION. Four properties deliberately differ from
// the source and are recorded here so nobody "fixes" them back:
//
//   - the top band slides in from the RIGHT; the source fills it from the LEFT
//     (x 0.0-0.30 goes new at f126, x 0.57-0.78 at f130-133)
//   - the bottom band slides in from the LEFT; the source gives no clean
//     directional reading at all (earliest at x 0.5-0.8, spreading both ways)
//   - the middle band starts after the outer ones; in the source it opens at
//     f123-124, SIMULTANEOUSLY with them, and runs throughout
//   - the top origin is 1/9; the source cannot say, because rows 136-184 —
//     exactly where 1/9 falls — are flat dark wall and return no reading in
//     either direction. The bottom origin DOES measure at ~8/9 (bands at
//     y 0.85-0.92).
//
// What IS measured and kept: the retreat is real (rows 264-320 read new at
// f123, old again at f138-143, new at f144); the middle band opens at y=0.497
// and stops on 1/3 and 2/3 to within half a percent; the growth curve is a
// clean S (0.035/0.082/0.165/0.294/0.482/0.671/0.806/0.918/0.976), hence
// ease-in-out everywhere; the edges are hard, hence no feather.
/**
 * Each phase's length. Four phases, staggered half a phase apart after P1.
 *
 * THE GEOMETRY FILLS THE WHOLE WINDOW. The transition spans 10-18% of the reel,
 * which is 69 frames, and all 69 carry shape: P1 opens on frame 0 and P4 lands
 * on frame 69. 23 is that constraint solved, not a taste choice — four phases
 * half-overlapping across 69 frames is 3 x 23.
 *
 * This was wrong twice, the same way both times, and both times the window was
 * right while the distribution inside it was not. At 12 the geometry occupied
 * f33-69; at 18, f15-69. Each left a darkening-only pre-roll that nobody asked
 * for and that the preview cannot even show (its outgoing layer is black), so
 * the shape read as faster than the source despite the transition's total
 * length being correct. There is no pre-roll now.
 */
const CASCADE_PHASE_FRAMES = 23;
/** Phase starts: half a phase apart, P1 on frame 0, P4 ending on 69. */
const CASCADE_EXPAND_AT = 0;
const CASCADE_RETREAT_AT = 23;
const CASCADE_MIDDLE_AT = 34;
const CASCADE_SLIDE_AT = 46;
/**
 * Bands OVERHANG the frame, and that is the whole trick.
 *
 * A band growing symmetrically about y=1/9 cannot also land exactly on
 * [0, 1/3]: its lower edge has to travel 2/9 while its upper edge travels 1/9.
 * Letting it extend past the frame keeps it centred on its origin line for the
 * whole expansion while the VISIBLE part lands exactly on the third. The
 * renderer permits this — `pctOffset` is explicitly unclamped for negative
 * offsets and `pctSize` is not capped at 1 — so `{y: -1/9, h: 4/9}` spans
 * -0.111 to 0.333 and reads as exactly the top third.
 *
 * Getting this wrong is silent: a clamped band would still cover the third, it
 * would just grow lopsidedly, which reads as a mistimed ease rather than as
 * wrong geometry.
 */
const TOP_OPEN = { x: 0, y: -1 / 9, w: 1, h: 4 / 9 };
const TOP_SHUT = { x: 0, y: 1 / 9, w: 1, h: 0 };
const BOTTOM_OPEN = { x: 0, y: 2 / 3, w: 1, h: 4 / 9 };
const BOTTOM_SHUT = { x: 0, y: 8 / 9, w: 1, h: 0 };
const cascadeBand = (channel, frame, from, to) => ({
    channel,
    from: 0,
    to: 1,
    duration: CASCADE_PHASE_FRAMES,
    easing: 'ease-in-out',
    anchor: { at: 'start', frame },
    mask: { shape: 'rect', from, to },
});
/**
 * Seven tracks on THREE channels, not five channels.
 *
 * The top band's expand, retreat and slide are disjoint in time, so they are
 * sequential tracks on one channel — `evaluateMotion` resolves those by
 * later-starting-track-wins, and the lint only rejects OVERLAPPING windows.
 * The bottom band's three ride `mask2` the same way. Only the middle band
 * straddles the others, so it is the only one that needed a new channel.
 *
 * The slide tracks lerp x, w, y and h in ONE track, which is what makes
 * "lands horizontally at the same moment it lands vertically" structural
 * rather than a pair of durations someone has to keep in sync.
 */
const THIRDS_CASCADE = [
    // P1 — bands open at the ninths, out to the outer thirds.
    cascadeBand('mask', CASCADE_EXPAND_AT, TOP_SHUT, TOP_OPEN),
    cascadeBand('mask2', CASCADE_EXPAND_AT, BOTTOM_SHUT, BOTTOM_OPEN),
    // P2 — and close again onto the same origin lines.
    cascadeBand('mask', CASCADE_RETREAT_AT, TOP_OPEN, TOP_SHUT),
    cascadeBand('mask2', CASCADE_RETREAT_AT, BOTTOM_OPEN, BOTTOM_SHUT),
    // P3 — the middle third, opening from the centre. Starts while P2 is half
    // done, so the frame approaches the bare outgoing clip without reaching it.
    // No overhang needed: the middle third is already symmetric about y=1/2.
    cascadeBand('mask3', CASCADE_MIDDLE_AT, { x: 0, y: 1 / 2, w: 1, h: 0 }, { x: 0, y: 1 / 3, w: 1, h: 1 / 3 }),
    // P4 — a 1/9-height band enters the top third from the RIGHT (left edge
    // sweeps x=1 to x=0, right edge pinned) and the bottom third from the LEFT
    // (left edge pinned, right edge sweeps 0 to 1), each growing to full
    // third-height as it goes.
    cascadeBand('mask', CASCADE_SLIDE_AT, { x: 1, y: 1 / 18, w: 0, h: 1 / 9 }, TOP_OPEN),
    cascadeBand('mask2', CASCADE_SLIDE_AT, { x: 0, y: 5 / 6, w: 0, h: 1 / 9 }, BOTTOM_OPEN),
];
/**
 * The slide, once. `dx` / `dy` are where the element travels FROM, in px.
 *
 * Defaults to `slide-up`'s vector so that `{ token: 'slide' }` with no params
 * is a usable animation rather than a bare fade — a recipe has to be total
 * over its params, and "no direction" is not a slide.
 */
const slideRecipe = (p) => {
    const dx = p.dx ?? 0;
    const dy = p.dx === undefined && p.dy === undefined ? SLIDE_DISTANCE : (p.dy ?? 0);
    return [
        fade(),
        ...(dy !== 0
            ? [
                {
                    channel: 'y',
                    from: dy,
                    to: 0,
                    duration: DEFAULT_MOTION_FRAMES,
                    easing: 'spring-soft',
                },
            ]
            : []),
        ...(dx !== 0
            ? [
                {
                    channel: 'x',
                    from: dx,
                    to: 0,
                    duration: DEFAULT_MOTION_FRAMES,
                    easing: 'spring-soft',
                },
            ]
            : []),
    ];
};
/** How long a ghost card takes to travel into place. Identical in all six
 *  hand-written copies. */
const GHOST_ENTRY_FRAMES = 42;
/** Where it comes to rest before the slow drift takes over. */
const GHOST_REST_X = 48;
/** The shared fade: up fast, settle translucent, out at the end. Not affected
 *  by `entryDelay` — the ghost is already visible before it travels. */
const ghostEnvelope = () => [
    { channel: 'opacity', from: 0, to: 0.9, duration: 18, easing: 'linear' },
    {
        channel: 'opacity',
        from: 0.9,
        to: 0.55,
        duration: 60,
        easing: 'ease-in-out',
        anchor: { at: 'start', frame: 18 },
        // The ghost BREATHES while it sits there — this is what stops it reading
        // as a static translucent plate. Easy to lose when hand-copying, and all
        // six copies had it.
        repeat: { mode: 'ping-pong', everyFrames: 120 },
    },
    {
        channel: 'opacity',
        from: 0.55,
        to: 0,
        duration: 16,
        easing: 'linear',
        anchor: { at: 'end', frame: 16 },
    },
];
/** The slow leftward slide after the card lands. `drift` is how long it lasts,
 *  which is simply how long that step is on screen. */
const ghostDrift = (p, from) => [
    {
        channel: 'x',
        from: GHOST_REST_X,
        to: -40,
        duration: p.drift ?? 120,
        easing: 'linear',
        anchor: { at: 'start', frame: from },
    },
];
export const MOTION_PRESETS = {
    // Static — visible immediately, no motion at all. The minimum desugaring:
    // an empty track list costs nothing in a stored RenderDoc.
    none: [],
    // ── Ghost cards (parametric) ──────────────────────────────────────
    //
    // Measured off `step-by-step`, where these two recipes were written out by
    // hand six times. Every number below appears in all six copies; the only
    // things that varied were `entryDelay` and `drift`, which is why they are
    // the params.
    //
    // The opacity envelope is shared and is NOT delayed by `entryDelay`: the
    // ghost fades up first and travels later, which is the whole reason
    // `delayFrames` could not express this — it would have moved the fade too.
    'ghost-rise': (p) => [
        ...ghostEnvelope(),
        { channel: 'rotate', from: 90, to: 90, duration: 1, easing: 'linear' },
        {
            channel: 'rotate',
            from: 90,
            to: 0,
            duration: GHOST_ENTRY_FRAMES,
            easing: 'ease-in-out',
            anchor: { at: 'start', frame: p.entryDelay ?? 0 },
        },
        { channel: 'x', from: 340, to: 340, duration: 1, easing: 'linear' },
        {
            channel: 'x',
            from: 340,
            to: GHOST_REST_X,
            duration: GHOST_ENTRY_FRAMES,
            easing: 'ease-in-out',
            anchor: { at: 'start', frame: p.entryDelay ?? 0 },
        },
        ...ghostDrift(p, (p.entryDelay ?? 0) + GHOST_ENTRY_FRAMES),
        {
            channel: 'y',
            from: -147,
            to: -447,
            duration: 20,
            easing: 'ease-out',
            anchor: { at: 'start', frame: 0 },
        },
        {
            channel: 'y',
            from: -447,
            to: 757,
            duration: GHOST_ENTRY_FRAMES,
            easing: 'ease-in-out',
            anchor: { at: 'start', frame: p.entryDelay ?? 0 },
        },
    ],
    'ghost-fly-in': (p) => [
        ...ghostEnvelope(),
        { channel: 'y', from: -697, to: -697, duration: 1 },
        {
            channel: 'x',
            from: 1250,
            to: GHOST_REST_X,
            duration: GHOST_ENTRY_FRAMES,
            easing: 'ease-out',
            anchor: { at: 'start', frame: p.entryDelay ?? 0 },
        },
        ...ghostDrift(p, (p.entryDelay ?? 0) + GHOST_ENTRY_FRAMES),
    ],
    'fade-in': [fade()],
    // ONE SLIDE, four names.
    //
    // These were four preset entries whose only difference was an axis and a
    // sign — the same fade, the same distance, the same spring. `slide` is the
    // recipe; the four directional tokens are aliases that call it with the
    // vector they always meant.
    //
    // The aliases stay because they are cited by 22 files, most of them shipped
    // built-in templates. Renaming those would be a large, risky edit to
    // production formats to win a shorter token list — and the win that actually
    // matters, one implementation instead of four, is had without it. A document
    // that wants a direction the four names cannot say now writes
    // `{ token: 'slide', params: { dx, dy } }` and gets any angle.
    //
    // `dx` / `dy` are where the element travels FROM, in px, so the signs match
    // the tracks they replace.
    slide: (p) => slideRecipe(p),
    'slide-up': (p) => slideRecipe({ dy: SLIDE_DISTANCE, ...p }),
    'slide-down': (p) => slideRecipe({ dy: -SLIDE_DISTANCE, ...p }),
    'slide-left': (p) => slideRecipe({ dx: SLIDE_DISTANCE, ...p }),
    'slide-right': (p) => slideRecipe({ dx: -SLIDE_DISTANCE, ...p }),
    pop: [
        fade(),
        {
            channel: 'scale',
            from: 0.85,
            to: 1,
            duration: DEFAULT_MOTION_FRAMES,
            easing: 'spring-pop',
        },
    ],
    'spring-in': [
        fade(),
        {
            channel: 'scale',
            from: 0.92,
            to: 1,
            duration: DEFAULT_MOTION_FRAMES,
            easing: 'spring-firm',
        },
    ],
    // A whole-element zoom, not an entrance: fully opaque throughout.
    'ken-burns': [
        {
            channel: 'scale',
            from: 1.0,
            to: 1.12,
            duration: 'block',
            easing: 'linear',
            origin: '50% 50%',
        },
    ],
    // The wipe IS the entrance — opacity stays at identity.
    'mask-wipe': [
        { channel: 'clip', from: 0, to: 1, duration: DEFAULT_MOTION_FRAMES },
    ],
    // `reveal` is handed to the block, which slices characters.
    typewriter: [
        { channel: 'reveal', from: 0, to: 1, duration: DEFAULT_MOTION_FRAMES },
    ],
    // Same channel, different block interpretation: words, not characters. The
    // element-level style stays at identity so it doesn't fight the per-word
    // animation the block runs.
    // The duration is the WHOLE ASSEMBLY, not one word's ramp — see
    // `wordAssemblySpanFrames`. It is 8 rather than DEFAULT_MOTION_FRAMES
    // because that is what the source measures: four words, 2 frames apart,
    // each ramping over 2.
    'word-by-word': [
        { channel: 'reveal', from: 0, to: 1, duration: WORD_REVEAL_SPAN_FRAMES },
    ],
    // Same track as `word-by-word`: the direction is not a motion property, it is
    // which word gets which turn, and that lives in the block that splits the
    // string. MEASURED off skin-edit 5.03-5.17s — the top line assembles
    // right-to-left ("our", "of", "any", "Book"), ~1 frame apart, each word
    // ramping in ~2 frames. The word "our" does not move a pixel across the whole
    // assembly, which is what rules out the "slide" this was catalogued as.
    'word-by-word-reverse': [
        { channel: 'reveal', from: 0, to: 1, duration: WORD_REVEAL_SPAN_FRAMES },
    ],
    // Same `reveal` mechanism again, third block interpretation: LETTERS, in a
    // scattered order. The track spans the whole assembly (last letter's start +
    // its ramp) rather than one letter's ramp, because `reveal` means "how far
    // through this element's reveal are we" and the line is not assembled until
    // the last glyph has finished.
    //
    // Deliberately NO opacity track: the per-letter opacities are the animation,
    // and an element-level fade on top would multiply into every glyph and wash
    // the scatter out. `typewriter` and `word-by-word` sit at identity for the
    // same reason.
    'letter-fade-scattered': [
        {
            channel: 'reveal',
            from: 0,
            to: 1,
            duration: LETTER_SCATTER_SPREAD_FRAMES + LETTER_SCATTER_FADE_FRAMES,
        },
    ],
    // Fourth block interpretation of the same `reveal` mechanism: LETTERS again,
    // but in reading order and rotating rather than fading. Deliberately no
    // opacity track, for the reason spelled out on `letter-fade-scattered` above
    // — the per-glyph opacities ARE the animation and an element-level fade would
    // multiply into every one of them.
    'letter-flip': [
        { channel: 'reveal', from: 0, to: 1, duration: LETTER_FLIP_SPAN_FRAMES },
    ],
    // ── Shaped-mask media transitions ─────────────────────────────────
    //
    // Geometry is a fraction of the element's own box on each axis, so on a
    // full-frame media-track these are fractions of the frame — which is the only
    // place either of these belongs.
    // MEASURED off the source (skin-edit 0-2s), not guessed. The first version of
    // this preset was written from a verbal description and had both the shape and
    // the rate wrong: rx 0.62 / ry 0.95 over 18 frames, against a source that opens
    // over ~54.
    //
    // What the frames actually say: the reveal front is centred on (0.50, 0.48) and
    // grows from ~0.03 to ~0.55 of the frame, with coverage climbing near-linearly
    // — and since coverage is an AREA, a linear area means a decelerating radius,
    // which is why the easing is ease-out rather than linear.
    //
    // ry/h ~= rx/w ~= 1.0 across the whole reliable range. In NORMALISED box
    // fractions the shape is therefore circular; it reads as a tall oval only
    // because the frame is 9:16. That is precisely the behaviour MotionMaskPart
    // documents as a limitation (axes are independent fractions of the element
    // box, so an ellipse does not stay circular on a non-square element) — here it
    // is the wanted behaviour, and writing rx != ry would have made it WRONG.
    //
    // `to` overshoots the measured 0.55 because the corners of a rect are at ~0.71
    // from centre: the measured figure is where the fit degenerates at ~98%
    // coverage, not where the animation should stop.
    //
    // The edge is SOFT, and that is the loudest thing about it. A radial alpha
    // profile of the source reads 0.77 at r=0.5, 0.54 at r=0.8, 0.20 at r=1.0 and
    // 0.01 at r=1.2 — a falloff roughly 0.6 of the radius wide. Rendering this
    // hard-edged, which the first version did, does not read as the same
    // transition at all.
    //
    // The CORNERS also lag by about a second: at 1.4s the centre is at 0.95 while
    // r=1.3 is still at 0.01, and they do not fill until ~2.0-2.4s. That is a
    // consequence of the soft edge plus the overshoot in `to`, not a second
    // animation — a wide feather on a shape that keeps growing past the frame is
    // exactly what leaves the corners last.
    // TWO STAGES, unioned — which is what the source actually does and what no
    // single shape could fit:
    //
    //   1. an OVAL opens quickly. The centre reaches 0.92 of its settled
    //      brightness by 1.0s and is essentially done.
    //   2. the RECTANGLE then bleeds out to the edges. The outer band sits at
    //      0.25 at 1.0s and only reaches 0.71 by 1.5s — a LATE acceleration,
    //      which is a second front arriving, not the tail of the first.
    //
    // Fitting one ellipse to both stages is why every previous version was wrong
    // in one half or the other: matched to the centre it blew the edges out too
    // early, matched to the edges it crawled. `maskComposite: 'add'` unions the
    // two parts, so stage 2 can start while stage 1 is still opening.
    'oval-iris': [
        {
            // ONE superellipse sweeping open, replacing the two-channel oval+rect
            // union this used to be. That version was a UNION OF TWO SILHOUETTES and
            // read as one — the rectangle appeared rather than the oval becoming it.
            //
            // Fitting the source's revealed region per frame against both a
            // superellipse and a plain ellipse (the control) gives:
            //
            //     t     covered   fitted n   resid   best-ellipse resid
            //   0.27s     0.06        1.5    0.069        0.072
            //   0.53s     0.33        6      0.128        0.147
            //   0.93s     0.53       12      0.103        0.140
            //   1.20s     0.65       12      0.070        0.152
            //   1.33s     0.72       16      0.088        0.144
            //
            // It is an OVAL only for the first half-second; from 0.9s the fitted
            // exponent is 12-16, i.e. a rounded rectangle. The superellipse beats the
            // best possible ellipse by up to 2x, which is why this is one morphing
            // shape rather than two unioned.
            channel: 'mask',
            from: 0,
            to: 1,
            // ease-out: the oval stage is QUICK and the rounded-rect expansion SLOW,
            // which is one curve, not two stages. ease-in-out over 34f both S-curved
            // it and finished in 1.1s; linear over 57f left it looking oval until
            // 1.2s.
            //
            // 36 frames (1.2s) against the source's own 1.9s. Matching the source
            // exactly measures best on the reveal curve — and plays too slow in a
            // template that has to land a hook, which is why this is deliberately
            // ~40% quicker. score-effect.py WILL report a worse distance for this
            // preset than a source-matched one; that is the intended trade, not a
            // regression, so do not "fix" it back to the measurement.
            duration: 36,
            easing: 'ease-out',
            mask: {
                shape: 'superellipse',
                // A BIG feather hides the corners and the shape reads as an oval however
                // high `n` climbs — measured, feather 0.18 held the fitted exponent at 2
                // until 1.2s while the source was already at 8 by 0.6s.
                feather: 0.09,
                from: { cx: 0.5, cy: 0.48, rx: 0.1, ry: 0.1, n: 2 },
                // rx is a fraction of the FULL box, so 0.5 exactly reaches the left and
                // right edges. Ending just past it means the corners arrive at the very
                // end rather than a second early, which keeps the late expansion slow
                // instead of snapping open.
                to: { cx: 0.5, cy: 0.48, rx: 0.53, ry: 0.56, n: 16 },
            },
        },
        {
            // The zoom-out the source runs UNDERNEATH the iris. It lives here, on the
            // same preset, rather than as the block's `kenBurns` field because
            // `kenBurns` has no duration and no easing — it is a linear zoom across
            // the WHOLE clip, so it cannot be made to finish with the mask. Sharing
            // the mask's 36 frames and ease-out is what makes the two read as one
            // move instead of a reveal with a drift behind it.
            //
            // 1.35 -> 1.0 is a deliberately big push. `scale` composes additively with
            // anything else on the channel, so a block that ALSO sets `kenBurns` will
            // have this win (an authored track supersedes the folded-in field).
            channel: 'scale',
            from: 1.35,
            to: 1.0,
            duration: 36,
            easing: 'ease-out',
        },
    ],
    // MEASURED off the source (skin-edit f124-f152 @30fps) by classifying every
    // pixel as outgoing-shot or incoming-shot against a settled reference either
    // side of the cut, then reading the two blocks' edges directly.
    //
    //   A  anchored TOP-RIGHT. Grows f124 -> f145 and settles at four fifths.
    //   B  anchored BOTTOM-LEFT. Starts f137 (12 frames later), runs 14 frames,
    //      and goes all the way to 1.0 — which is what closes the frame.
    //
    // The load-bearing measurement is that for BOTH blocks w and h are equal at
    // every frame, to within the noise floor (w/h = 1.00 +/- 0.03 across f126-f142
    // for A). They are squares in BOX FRACTION, not in pixels — so on a 9:16 frame
    // each block is a portrait rect the shape of the frame, scaling from its
    // corner. Because x+w and y+h are each pinned to a constant, the corner
    // anchoring survives the linear lerp for free.
    //
    // This is the second rewrite. Version one came from prose. Version two came
    // from a measurement that summed coverage PER HALF-FRAME, which presupposed a
    // horizontal split and so could only ever return one — it reported two
    // half-height bars, and two half-height bars is what it rendered: a stack, not
    // an L. The gap between A settling at 0.8 and B starting to close is the L.
    'corner-l-wipe': [
        {
            channel: 'mask',
            from: 0,
            to: 1,
            duration: 20,
            // Measured u against normalised time tracks smoothstep closely (0.13/0.29/
            // 0.56/0.80 at tau 0.19/0.33/0.48/0.62); ease-out would already be at 0.35
            // by tau 0.19, which the frames rule out.
            easing: 'ease-in-out',
            mask: {
                shape: 'rect',
                from: { x: 1, y: 0, w: 0, h: 0 },
                to: { x: 0.2, y: 0, w: 0.8, h: 0.8 },
            },
        },
        {
            // The opposite corner, 12 frames later. `anchor` is the whole sequencing
            // mechanism — and the reason this needs a second channel, since one
            // channel cannot hold two clocks.
            //
            // ease-IN, not out: the bottom-left corner column (which A never reaches,
            // so the reading is clean) measures 0.07/0.16/0.34/0.54 at tau
            // 0.29/0.50/0.71/0.86 — accelerating throughout, close to tau^3.
            channel: 'mask2',
            from: 0,
            to: 1,
            duration: 14,
            easing: 'ease-in',
            anchor: { at: 'start', frame: 12 },
            mask: {
                shape: 'rect',
                from: { x: 0, y: 1, w: 0, h: 0 },
                to: { x: 0, y: 0, w: 1, h: 1 },
            },
        },
    ],
    // ── The BAR family ────────────────────────────────────────────────
    //
    // A band growing across the frame from one edge. Geometry is stated rather
    // than measured off one reel: an edge wipe has only one honest reading —
    // start as a zero-height (or zero-width) band ON the edge and finish covering
    // the frame — and the measured examples that motivated these all fit that
    // description with confidences too low to calibrate anything finer.
    //
    // 18 frames because that is the median duration of the seven shaped
    // transitions measured on the reel that asked for them (14-23 frames).
    'bar-wipe-up': [barWipe({ x: 0, y: 1, w: 1, h: 0 })],
    'bar-wipe-down': [barWipe({ x: 0, y: 0, w: 1, h: 0 })],
    'bar-wipe-right': [barWipe({ x: 0, y: 0, w: 0, h: 1 })],
    'bar-wipe-left': [barWipe({ x: 1, y: 0, w: 0, h: 1 })],
    // Two bars parting from the centre — the shape the one confidently measured
    // shaped transition in that reel fitted (rect2, gain 0.71). Two channels
    // because the halves are two rectangles, and one channel cannot carry two.
    'split-bars': [
        {
            channel: 'mask',
            from: 0,
            to: 1,
            duration: 18,
            easing: 'ease-in-out',
            mask: {
                shape: 'rect',
                from: { x: 0.5, y: 0, w: 0, h: 1 },
                to: { x: 0, y: 0, w: 0.5, h: 1 },
            },
        },
        {
            channel: 'mask2',
            from: 0,
            to: 1,
            duration: 18,
            easing: 'ease-in-out',
            mask: {
                shape: 'rect',
                from: { x: 0.5, y: 0, w: 0, h: 1 },
                to: { x: 0.5, y: 0, w: 0.5, h: 1 },
            },
        },
    ],
    'thirds-cascade': THIRDS_CASCADE,
    // ── wedge-cross ───────────────────────────────────────────────────
    //
    // Two translucent wedges scissoring apart. See the token's entry in
    // animation-tokens.ts for the measurement; the geometry notes are here.
    //
    // WHY ±10.36° AND NOT ±18°. The mask viewBox is 0-100 on BOTH axes under
    // `preserveAspectRatio="none"`, so it is stretched to the element box and an
    // angle in here is not the angle on screen. A direction (cos θ, sin θ) in box
    // units lands on screen as (cos θ·W, sin θ·H), so
    //
    //     screen angle = atan(tan θ · H/W) = atan(tan θ · 1280/720)
    //
    // and the measured 18.0° needs atan(tan(18°)/1.778) = 10.36° in here. STATED
    // LIMITATION, inherited from the viewBox and not introduced by this preset:
    // that arithmetic is 9:16-specific, so on a landscape composition these
    // wedges subtend a different on-screen angle. Every mask in this registry has
    // the same property; this is the first one where it is visible as an angle
    // rather than as a slightly-oval iris.
    //
    // The rects are deliberately OVERSIZED (w:3 spanning x -1..2, h:2.4). Two
    // reasons, both load-bearing: rotation about the box centre swings the
    // corners by up to (w/2)·sin(10.36°) ≈ 0.27 of the box, so a rect parked just
    // off-frame would poke a corner back INTO it at progress 0; and the travel
    // has to carry the far edge fully past the frame or the wedge stops short and
    // leaves a sliver of the outgoing clip pinned in a corner.
    //
    // ease-out because the measured offsets decelerate: the +18° edge steps
    // -102, -86, -102, -105, -109, -95, -111, -77, -64, -53, -48, -38, -36, -31.
    'wedge-cross': [
        {
            // Enters from the BOTTOM; its top edge rises. This is the +18° edge.
            channel: 'mask',
            from: 0,
            to: 1,
            duration: 20,
            easing: 'ease-out',
            mask: {
                shape: 'rect',
                from: { x: -1, y: 1.5, w: 3, h: 2.4, rotate: 10.36, alpha: 0.5 },
                to: { x: -1, y: -0.5, w: 3, h: 2.4, rotate: 10.36, alpha: 0.5 },
            },
        },
        {
            // Enters from the TOP; its bottom edge falls. This is the -18° edge.
            channel: 'mask2',
            from: 0,
            to: 1,
            duration: 20,
            easing: 'ease-out',
            mask: {
                shape: 'rect',
                from: { x: -1, y: -2.9, w: 3, h: 2.4, rotate: -10.36, alpha: 0.5 },
                to: { x: -1, y: -0.9, w: 3, h: 2.4, rotate: -10.36, alpha: 0.5 },
            },
        },
    ],
    // ── wedge-up-right ────────────────────────────────────────────────
    //
    // One opaque wedge. A triangle opens at the bottom-left corner and its
    // hypotenuse sweeps up and to the right. See the token's entry in
    // animation-tokens.ts for why this is STATED rather than measured.
    //
    // THE ANGLE. 33.83° in here, ~50° on screen. Same viewBox correction as
    // `wedge-cross` — the mask viewBox is 0-100 on both axes under
    // `preserveAspectRatio="none"`, so a box-space angle θ lands on screen as
    // atan(tan θ · H/W) = atan(tan θ · 1280/720). The 50° is read off the
    // gradient ORIENTATION of the placeholder artwork that prompted this
    // (doubled-angle mean over the wedge body, iso-lines at 50.4° from
    // horizontal), which is as much precision as a soft gradient supports —
    // hence the round number, and the same 9:16-specific caveat `wedge-cross`
    // carries applies here.
    //
    // THE SIGN. POSITIVE rotate. SVG rotates clockwise in a y-down space, so a
    // point at (+d, 0) from the centre maps to (d cos θ, d sin θ) and the right
    // end of the rect's top edge moves DOWN — a '\' hypotenuse, which is the one
    // that puts the growing triangle in the BOTTOM-LEFT. Negative would sweep the
    // identical shape out of the bottom-RIGHT instead. Both were plotted before
    // choosing; this is not a guess about a sign convention.
    //
    // THE TRAVEL. x and y lerp together (-2.9,1.33) -> (-1.5,-0.35), so the rect
    // moves right as it rises and the reveal reads as up-AND-right rather than
    // straight up. Most of that vector is along the edge's own direction and so
    // invisible; the ~1.3 of it that is perpendicular is what actually uncovers
    // the frame.
    //
    // OVERSIZED at w:4 h:3, for `wedge-cross`'s two reasons and more of both:
    // rotation about the box centre swings a corner by (w/2)·sin(33.83°) ≈ 1.11
    // of the box — six times wedge-cross's swing, because this angle is three
    // times steeper — so a snugly-parked rect would poke a corner back into frame
    // at progress 0. The endpoints are solved rather than eyeballed: `from` is
    // the first position with NO intersection with the unit square, `to` the
    // first that contains all four of its corners, each with 0.15 of margin.
    'wedge-up-right': [
        {
            channel: 'mask',
            from: 0,
            to: 1,
            duration: 20,
            // `wedge-cross`'s measured deceleration, not a fresh choice — see above.
            easing: 'ease-out',
            mask: {
                shape: 'rect',
                from: { x: -2.9, y: 1.33, w: 4, h: 3, rotate: 33.83 },
                to: { x: -1.5, y: -0.35, w: 4, h: 3, rotate: 33.83 },
            },
        },
    ],
    // ── light-bloom-in ────────────────────────────────────────────────
    //
    // MEASURED off reel-3 0-0.8s, as mean luminance against the settled frame:
    //
    //   f0    f2    f5    f8    f11   f14   f17   f20   f21
    //   0.43  0.72  1.00  1.21  1.28  1.23  1.14  1.01  0.99
    //
    // Two stages, because one tween cannot overshoot: up THROUGH 1.0 to a peak of
    // 1.28 at frame 11, then back down to 1.0 by frame 21. Sequential tracks on
    // one channel, the same mechanism `thirds-cascade` uses.
    //
    // Both easings were fitted against controls rather than named by eye. Stage
    // one: ease-out 0.012 residual against linear 0.141 and ease-in 0.293 — a
    // 12x margin, and 1-(1-t)^2 lands within 0.001 of the measurement at three
    // separate taus. Stage two is the honest close call: linear 0.058,
    // ease-in-out 0.072, ease-in 0.101. Linear wins and is the simpler claim.
    //
    // NO BLUR TRACK, and that is a correction rather than an omission. Variance
    // of the Laplacian collapses from 53 to 39.5 at the peak, which reads as
    // defocus and was very nearly built as one — but lapvar scales with contrast,
    // and the bloom flattens contrast by construction. A contrast-invariant
    // high-band/mid-band ratio says f0 is the SHARPEST frame in the sequence
    // (0.748, against 0.605 settled) and the peak dips only ~10%. There is no
    // defocus here; there is a contrast collapse that an uncorrected sharpness
    // measure reports as one.
    //
    // What this preset does NOT reproduce: at the peak the source's contrast
    // FALLS (std 0.89x) while its mean RISES (1.28x), which is a white lift, not
    // an exposure lift — a `brightness` multiply raises both together. Spelled
    // out in the catalogue. Matching it needs a white solid with an opacity
    // track over the clip, i.e. composition, not one token.
    'light-bloom-in': [
        {
            channel: 'brightness',
            from: 0.43,
            to: 1.28,
            duration: 11,
            easing: 'ease-out',
        },
        {
            channel: 'brightness',
            from: 1.28,
            to: 1,
            duration: 10,
            easing: 'linear',
            anchor: { at: 'start', frame: 11 },
        },
    ],
};
/**
 * Tracks a mask transition applies to the layer being UNCOVERED.
 *
 * Every other mask transition leaves the outgoing half empty on purpose — it
 * is the layer being revealed, and touching it dips the composite toward
 * black. `thirds-cascade` is the case where dipping toward black IS the
 * effect: the source's mean luminance runs 100 -> 34 -> 94 across the
 * transition, and the floor at 0.34 is the outgoing clip darkening under
 * bands that have not yet covered it.
 *
 * A SEPARATE table rather than a `layer` field on `MotionTrack`, because most
 * tokens are entrances on a text node where no outgoing layer exists — a
 * per-track `layer: 'outgoing'` would be a representable invalid state on
 * every one of them. Absent from this table means "outgoing untouched", so
 * all seven existing mask transitions keep their current behaviour by
 * construction.
 *
 * The ramp is ease-IN because the measurement says accelerating, not linear:
 * 1.00 / 0.92 / 0.83 / 0.75 at window frames 3/13/23/31, then 0.69 / 0.44 /
 * 0.34 at 33/38/42. Most of the fall happens in the last third. It bottoms at
 * frame 42, which is exactly where P1 finishes, and holds — the layer is fully
 * covered by then, so there is nothing to recover to.
 */
export const MASK_TRANSITION_OUTGOING = {
    'thirds-cascade': [
        {
            channel: 'brightness',
            from: 1,
            to: 0.34,
            // 42 is MEASURED — the composite's luminance floor sits at window frame
            // 42 — so it is stated, not derived from the phase constants. It was
            // briefly `CASCADE_EXPAND_AT + CASCADE_PHASE_FRAMES - 3`, which silently
            // moved the floor to 48 the moment the phases were re-timed. The
            // darkening is an independent measurement of the outgoing layer and has
            // no reason to follow the geometry's clock.
            duration: 42,
            easing: 'ease-in',
        },
    ],
};
/**
 * Exit presets. Every one is END-ANCHORED — that is the whole mechanism. There
 * is no separate exit system.
 */
export const EXIT_PRESETS = {
    none: [],
    'fade-out': [
        {
            channel: 'opacity',
            from: 1,
            to: 0,
            anchor: { at: 'end', frame: DEFAULT_EXIT_FRAMES },
            duration: DEFAULT_EXIT_FRAMES,
            easing: 'linear',
        },
    ],
    'slide-out-up': [
        {
            channel: 'opacity',
            from: 1,
            to: 0,
            anchor: { at: 'end', frame: DEFAULT_EXIT_FRAMES },
            duration: DEFAULT_EXIT_FRAMES,
            easing: 'linear',
        },
        {
            channel: 'y',
            from: 0,
            to: -SLIDE_DISTANCE,
            anchor: { at: 'end', frame: DEFAULT_EXIT_FRAMES },
            duration: DEFAULT_EXIT_FRAMES,
            easing: 'ease-in',
        },
    ],
    'pop-out': [
        {
            channel: 'opacity',
            from: 1,
            to: 0,
            anchor: { at: 'end', frame: DEFAULT_EXIT_FRAMES },
            duration: DEFAULT_EXIT_FRAMES,
            easing: 'linear',
        },
        {
            channel: 'scale',
            from: 1,
            to: 0.85,
            anchor: { at: 'end', frame: DEFAULT_EXIT_FRAMES },
            duration: DEFAULT_EXIT_FRAMES,
            easing: 'ease-in',
        },
    ],
};
/** 8 frames at 30fps — the historical DEFAULT_TRANSITION_FRAMES, as a time. */
const TRANSITION_SECONDS = 8 / 30;
const transitionTrack = (channel, from, to) => ({
    channel,
    from,
    to,
    duration: 'block',
    easing: 'linear',
});
export const TRANSITION_MOTION = {
    cut: { durationSeconds: 0, outgoing: [], incoming: [] },
    fade: {
        durationSeconds: TRANSITION_SECONDS,
        outgoing: [],
        incoming: [transitionTrack('opacity', 0, 1)],
    },
    slide: {
        durationSeconds: TRANSITION_SECONDS,
        // Percent of the clip box, not px — the renderer maps an `x` track on a
        // transition to a percentage translate because a clip fills its frame.
        outgoing: [transitionTrack('x', 0, -100)],
        incoming: [transitionTrack('x', 100, 0)],
    },
    wipe: {
        durationSeconds: TRANSITION_SECONDS,
        // The outgoing clip is uncovered from the left as the incoming one is
        // revealed from the right; `clip` is the one channel that already means
        // "horizontal inset reveal".
        outgoing: [transitionTrack('clip', 1, 0)],
        incoming: [transitionTrack('clip', 0, 1)],
    },
    // ── whip-up ───────────────────────────────────────────────────────
    //
    // The incoming clip rushes UP into place and decelerates in, motion-blurred
    // on the way.
    //
    // MEASURED off journey-2 f662-694 by translation phase correlation, frame by
    // frame. Displacement is up throughout and decays cleanly:
    //
    //   f662  f663  f666  f671  f675  f680  f684  f690  f694  f695
    //   -120  -127  -101   -78   -52   -26   -16   -11    -6     0   px/frame
    //
    // A short burst near-constant at 100-127 px/frame, then an exponential settle
    // of ratio 0.906 per frame. TOTAL TRAVEL 1.267 FRAME HEIGHTS over 33 frames,
    // which is what makes it a whip rather than a slide and why the clip reads as
    // passing more than once before it lands.
    //
    // WHAT THIS IS NOT. It looks like a spin and it is not one: log-polar phase
    // correlation returns ~0 degrees wherever its response is trustworthy (its one
    // large reading, -25 degrees at f663, sits on a response of 0.011 and is
    // noise), and a structure-tensor test over a ring of patches picks WHIP over
    // SPIN at every frame of the event by a residual of 17-37 against 50-69. The
    // streaks are parallel and vertical (smear +84 to +90 degrees, coherence
    // 0.90-0.97), not curved about a centre.
    //
    // `ease-out` is the closest curve in the registry's closed set; it runs
    // slightly behind the measurement early (at 27% of the window the source has
    // travelled 58% and ease-out gives ~50%). A spring was rejected outright —
    // the measured displacement is monotone with no overshoot.
    //
    // ── The 26.7% band, which is a KNOWN and ACCEPTED consequence ──────
    //
    // Both layers travel the measured 1.267 screens. Two stacked layers are
    // adjacent only at exactly one screen, so at 126.7 the gap between the
    // outgoing clip's bottom edge and the incoming clip's top edge is a CONSTANT
    // 26.7% of the screen, and it rides down the frame for the whole transition.
    //
    // It was tried at 100 (no band) and as a two-pass roll over a parked outgoing
    // clip (no band, gaps filled by the shot being replaced). Both were reviewed
    // against the source and judged further from it than this. The band stays by
    // authorial decision — do not "fix" it back to 100 without asking, because
    // that trade has already been made in the other direction twice.
    'whip-up': {
        // 33 frames at the 30fps it was measured at, carried as a time so a 60fps
        // render whips for the same 1.1s rather than twice as fast.
        durationSeconds: 33 / 30,
        outgoing: [
            {
                channel: 'y',
                from: 0,
                to: -126.7,
                duration: 'block',
                easing: 'ease-out',
            },
            // Blur tracks the SPEED, and the speed is highest at the start — hence
            // max blur at 0 decaying out, on both layers, rather than a symmetric
            // in-out that would be sharpest exactly where the motion is fastest.
            {
                channel: 'blur',
                from: 34,
                to: 0,
                duration: 'block',
                easing: 'ease-out',
            },
        ],
        incoming: [
            {
                channel: 'y',
                from: 126.7,
                to: 0,
                duration: 'block',
                easing: 'ease-out',
            },
            {
                channel: 'blur',
                from: 34,
                to: 0,
                duration: 'block',
                easing: 'ease-out',
            },
        ],
    },
};
/** The `from` anchor, as the transform-origin the renderer already used. */
const KEN_BURNS_ORIGIN = {
    left: '20% 50%',
    right: '80% 50%',
    center: '50% 50%',
};
export function kenBurnsToTracks(kb) {
    return [
        {
            channel: 'scale',
            from: kb.zoomFrom,
            to: kb.zoomTo,
            duration: 'block',
            easing: 'linear',
            origin: KEN_BURNS_ORIGIN[kb.from],
        },
    ];
}
// ── Desugaring ──────────────────────────────────────────────────────
/**
 * `Indexed<AnimationToken>` → one token. Mirrors the compiler's `pickIndexed`
 * for the two object payloads; a token is a string, so there is no `base` to
 * merge (see the note on `IndexedPartial`).
 */
function pickToken(token, index) {
    if (token === undefined)
        return undefined;
    if (typeof token === 'string')
        return token;
    const entries = token.byIndex;
    if (entries.length === 0)
        return undefined;
    return entries[index % entries.length];
}
/** Shifts a track's anchor by `delayFrames`, respecting which end it hangs off. */
function delayTrack(track, delayFrames) {
    if (delayFrames === 0)
        return track;
    const anchor = track.anchor ?? { at: 'start', frame: 0 };
    // A start anchor moves LATER; an end anchor is measured backwards from the
    // element's end, so delaying it means hanging it CLOSER to that end.
    const frame = anchor.at === 'start'
        ? (anchor.frame ?? 0) + delayFrames
        : Math.max(0, (anchor.frame ?? 0) - delayFrames);
    return { ...track, anchor: { at: anchor.at, frame } };
}
/** Applies a spec-level `durationFrames` override to a fixed-length track. */
function retimeTrack(track, durationFrames) {
    if (track.duration === 'block')
        return track;
    const anchor = track.anchor;
    // An end-anchored track's offset IS its length; retiming must move both or
    // the exit detaches from the element's end.
    if (anchor?.at === 'end' && anchor.frame === track.duration) {
        return {
            ...track,
            duration: durationFrames,
            anchor: { at: 'end', frame: durationFrames },
        };
    }
    return { ...track, duration: durationFrames };
}
/**
 * Desugars a MotionSpec into concrete tracks. Runs at PHASE B, never at render
 * time — the RenderDoc carries tracks, so a stored doc replays identically
 * after the preset table is retuned.
 *
 * Order (§2): token tracks first, then exit tracks, then hand-authored tracks.
 * Later tracks on the same channel win deterministically, so a template can
 * override one channel of a token without restating the rest.
 */
/**
 * The tracks a token stands for. THE ONLY WAY TO READ THE TABLE.
 *
 * Every consumer goes through this so that adding a recipe cannot break one of
 * them by changing what a table entry is. Callers that only want a token's
 * default shape pass no params.
 */
export function presetTracks(token, params = {}, presets = MOTION_PRESETS) {
    const preset = presets[token] ?? MOTION_PRESETS[token];
    if (!preset)
        return [];
    return typeof preset === 'function' ? preset(params) : preset;
}
export function desugarMotion(spec, presets = MOTION_PRESETS, index = 0) {
    const token = pickToken(spec.token, index);
    const tokenTracks = token
        ? presetTracks(token, spec.params ?? {}, presets)
        : [];
    const exitTracks = spec.exit ? (EXIT_PRESETS[spec.exit] ?? []) : [];
    const sugar = [...tokenTracks, ...exitTracks].map((track) => spec.durationFrames !== undefined
        ? retimeTrack(track, spec.durationFrames)
        : track);
    const all = [...sugar, ...(spec.tracks ?? [])];
    const delay = spec.delayFrames ?? 0;
    return all.map((track) => delayTrack(track, delay));
}
/**
 * Normalises the legacy `entrance` / `entranceDurationFrames` /
 * `entranceDelayFrames` field group into a MotionSpec. One shim, one place —
 * this is what keeps the change strictly additive (§11).
 *
 * Returns `undefined` when there is nothing to animate, so a compiler can keep
 * omitting `motion` entirely rather than baking an empty object onto every node.
 */
export function normalizeMotion(input) {
    if (input.motion) {
        // An explicit `motion` wins — but it no longer DISCARDS what the caller
        // also said. The legacy fields fill in only what the spec left unsaid.
        //
        // THIS IS A BUG CLASS, NOT A STYLE POINT. Returning `input.motion` outright
        // meant a caller that authored both silently lost the legacy half, and the
        // silence was total: the token was still right, the measured duration was
        // still sitting on the node, and only the pixels disagreed. The reel
        // derivation attached a measured horizontal slide as an `x` track and, by
        // doing so, retimed every one of those lines to its token's nominal span —
        // a measured 25-frame typewriter ran in 12, a two-word line assembled in 8
        // frames, and what a human saw was a fade where the source assembled word
        // by word.
        //
        // "Half-legacy/half-track is the one shape nobody can reason about" was the
        // original argument for discarding. But the merge below is not a half
        // shape: each field is a DEFAULT the spec may override, which is what a
        // caller supplying both plainly means. Dropping data on the floor is the
        // harder thing to reason about, because nothing reports it.
        //
        // Nothing shipped authors both, so no registered template moves a pixel.
        const merged = { ...input.motion };
        if (merged.token === undefined && input.entrance !== undefined) {
            merged.token = input.entrance;
        }
        if (merged.durationFrames === undefined &&
            input.entranceDurationFrames !== undefined) {
            merged.durationFrames = input.entranceDurationFrames;
        }
        if (merged.delayFrames === undefined &&
            input.entranceDelayFrames !== undefined) {
            merged.delayFrames = input.entranceDelayFrames;
        }
        return merged;
    }
    if (input.entrance === undefined)
        return undefined;
    const spec = { token: input.entrance };
    if (input.entranceDurationFrames !== undefined) {
        spec.durationFrames = input.entranceDurationFrames;
    }
    if (input.entranceDelayFrames !== undefined) {
        spec.delayFrames = input.entranceDelayFrames;
    }
    return spec;
}
/**
 * Every token has a preset. Asserted at runtime as well as by the Record type,
 * because the type lives in this package and the renderer is another one.
 */
export function hasMotionPreset(token) {
    return ANIMATION_TOKENS.includes(token);
}
//# sourceMappingURL=motion-presets.js.map